import Container, { Service } from 'typedi';
import ContextApi from '../../../APIs/ContextApi';
import { WindowToken } from '../../injection-tokens';
import { StorageItemEnum } from '../../models/enums/storage-enums';
import { LoggerProvider } from '../logger';
import {
    LocalSimpleStoreService,
    SessionSimpleStoreService,
} from '../storage/implementations/simple-store';
import { Utils } from '../utils';
import { ClickStreamTrackingProvider } from '../tracking';
import {
    EventFormatterBuilder,
    EventFormatterBuilderFactory,
} from '@888unifiedclient/event-formatter';
import { AuthenticatedPageContextData } from 'page-context-manager';
import { ISessionUserData } from './interfaces/ISessionUserData';
import { AuthenticationUtils } from '../authentication/utils/authentication.utils';
import { ILogger } from '@sparkware/uc-sdk-core';
import { ApiResponse } from '../../../Modules/Utils/API/Interfaces/IApi';
import { ApiErrorCodes, StatusCodes } from '../../models/enums/general-enums';
import { ILogout } from '../logout/models';
import { LoaderManager } from '../../loaders/LoaderManager';
import { ISessionManager } from './interfaces/ISessionManager';
import TokenApi from '../../../APIs/TokenApi';
import { SmartBannerService } from '../smart-banner/smart.banner.service';

@Service()
export class SessionState {
    private _authenticatedContextDataResponse: Promise<
        ApiResponse<AuthenticatedPageContextData, any>
    >;
    private _tokenResponse: Promise<any>;
    private readonly _window: Window;
    private readonly _logger: ILogger;
    private readonly _localSimpleStoreService: LocalSimpleStoreService;
    private readonly _sessionSimpleStoreService: SessionSimpleStoreService;
    private readonly _utils: Utils;
    private readonly _clickStreamTrackingProvider: ClickStreamTrackingProvider;
    private readonly _eventFormatterBuilder: EventFormatterBuilder;
    private readonly _authenticationUtils: AuthenticationUtils;
    private readonly _smartBannerService: SmartBannerService;
    private get _logoutPromise(): Promise<ILogout> {
        return LoaderManager.Instance.LogoutLoader.Instance;
    }

    private get _sessionManagerPromise(): Promise<ISessionManager> {
        return LoaderManager.Instance.SessionManagerLoader.Instance;
    }

    public get authenticatedContextDataResponse() {
        return this._authenticatedContextDataResponse;
    }

    public get tokenResponse() {
        return this._tokenResponse;
    }

    constructor() {
        this._window = Container.get(WindowToken);
        this._logger = Container.get(LoggerProvider).getLogger('SessionState');
        this._localSimpleStoreService = Container.get(LocalSimpleStoreService);
        this._sessionSimpleStoreService = Container.get(SessionSimpleStoreService);
        this._utils = Container.get(Utils);
        this._clickStreamTrackingProvider = Container.get(ClickStreamTrackingProvider);
        const eventFormatterBuilderFactory = Container.get(EventFormatterBuilderFactory);
        this._eventFormatterBuilder =
            eventFormatterBuilderFactory.createEventFormatterBuilder('SessionState');
        this._authenticationUtils = Container.get(AuthenticationUtils);
        this._smartBannerService = Container.get(SmartBannerService);
        void this._getAuthenticationContextData();
    }

    private _getAuthenticationContextData = async () => {
        this._authenticatedContextDataResponse = ContextApi.GetAuthenticatedContextData();
        this._tokenResponse = TokenApi.GetToken();
        const authenticatedContextDataResponse = await this._authenticatedContextDataResponse;
        const { errorResponse } = authenticatedContextDataResponse;
        const tokenResponse = await this._tokenResponse;
        if (!errorResponse && tokenResponse?.Token) {
            await this._isRefreshUserState(authenticatedContextDataResponse, tokenResponse);
        } else {
            await this._resetUserStateStorage();
        }
        this._smartBannerService.registerSmartBannerService();
        // without this indication, spectate, sgp and other clients will not be able to resolve
        // and call getClientSettings with correct authenticated context data
        this._window.userDataStoreDO.resolve();
    };

    private _isRefreshUserState = async (
        authenticatedContextDataResponse: ApiResponse<AuthenticatedPageContextData, any>,
        tokenResponse: any,
    ): Promise<void> => {
        const isNativeSdk = this._utils.isNativeSDK();
        if (isNativeSdk || this._utils.isBossMode()) return;
        await this._refreshUserState(authenticatedContextDataResponse, tokenResponse);
    };

    private async _resetUserStateStorage(): Promise<void> {
        this._authenticationUtils.removeAuthorizationData();
        this._localSimpleStoreService.remove(StorageItemEnum.GeolocationData);
        this._localSimpleStoreService.remove(StorageItemEnum.PostLoginActionsInterrupted);
        this._localSimpleStoreService.remove(StorageItemEnum.PostLoginActionsRegistrationAutoLogin);
        const userSessionStorageItems = [StorageItemEnum.IsPostLoginActivityHistoryProcessed];
        userSessionStorageItems.forEach((item) => this._sessionSimpleStoreService.remove(item));
        await (await this._logoutPromise)?.removeLobbyCookie();
    }

    private _setUserStateStorage(authenticatedPageContextdata: AuthenticatedPageContextData): void {
        this._window.pageContextManager.updatePageContextAuthenticated(
            authenticatedPageContextdata,
        );
        const cid = authenticatedPageContextdata.user?.cid;
        if (cid) {
            this._sessionSimpleStoreService.set(StorageItemEnum.CID, cid);
            this._sessionSimpleStoreService.set(StorageItemEnum.LoginNotification, 'true');
        }
    }

    private _getUserData = async (
        authenticatedContextDataResponse: ApiResponse<AuthenticatedPageContextData, any>,
        tokenResponse: any,
    ): Promise<Partial<ApiResponse<ISessionUserData, any>>> => {
        try {
            const storeStateResponse = await ContextApi.GetStoreState();
            if (
                authenticatedContextDataResponse.errorResponse ||
                storeStateResponse.errorResponse ||
                !tokenResponse
            ) {
                return {
                    errorResponse: [
                        authenticatedContextDataResponse.errorResponse,
                        storeStateResponse.errorResponse,
                        !tokenResponse ? 'Error while getting token' : undefined,
                    ].filter((error) => !!error),
                };
            }

            const authenticatedData = authenticatedContextDataResponse.response;
            return {
                response: {
                    AuthenticateData: {
                        CID: authenticatedData.user.cid,
                        Token: tokenResponse.Token,
                        RegulationTypeID: authenticatedData.regulation.regulationTypeId,
                        RestrictedApps: [],
                        BatchNumber: authenticatedData.user.batchNumber,
                        HasKambiBets: authenticatedData.user.hasKambiBets || false,
                    },
                    PageContextData: authenticatedContextDataResponse.response,
                    StoreState: storeStateResponse.response,
                },
            };
        } catch (e) {
            this._logger.error('[_getUserData]', e);
            return null;
        }
    };

    private _refreshUserState = async (
        authenticatedContextDataResponse: ApiResponse<AuthenticatedPageContextData, any>,
        tokenResponse: any,
    ): Promise<void> => {
        const userData = await this._getUserData(authenticatedContextDataResponse, tokenResponse);

        if (userData.response) {
            this._logger.debug(
                `[_refreshUserState] Unified Client Service refresh state details successfully retrieved!, response: ${userData.response}`,
            );
            this._setUserStateStorage(userData.response.PageContextData);
            const sessionManager = await this._sessionManagerPromise;
            await sessionManager.refreshSessionUserData(userData.response);
        } else {
            this._logger.debug(
                `[_refreshUserState] Unified Client Service refresh state details successfully retrieved!, errorResponse: ${userData?.errorResponse}`,
            );
            this._handleUserRefreshStateError(userData.errorResponse);
        }
    };

    private _handleUserRefreshStateError = (errorResponse: Array<any>) => {
        this._logger.debug('Unified Client Service refresh state details could not be retrieved!');

        const isTerminatedSession = errorResponse.some(
            (error) =>
                (error?.status == StatusCodes.BAD_REQUEST &&
                    error?.data?.error?.code === ApiErrorCodes.MissingUserData) ||
                error?.status == StatusCodes.UNAUTHORIZED ||
                error?.status == StatusCodes.FORBIDDEN,
        );

        if (isTerminatedSession) {
            const formatter = this._eventFormatterBuilder.createFormatter(
                '_handleUserRefreshStateError',
            );
            this._resetUserStateStorage();
            const eventData = formatter.formatUCEvent(
                {
                    message: 'Session was terminated while the browser was in background',
                },
                { correlationID: this._utils.generateCorrelationID() },
                { event: 'page-refresh-due-to-out-of-sync-sessions' },
            );

            this._clickStreamTrackingProvider.sendEventV2(eventData);
        }
    };
}
